import { formatDate } from '@/lib/format';
import type { ReactNode } from 'react';

const CONTAINER = 'mx-auto max-w-3xl px-4 sm:px-6';

interface LegalDocumentProps {
  eyebrow: string;
  title: string;
  updatedOn: string;
  summary: ReactNode;
  children: ReactNode;
}

// The frame for the privacy policy and the terms: a heading, the date it last
// changed, a short summary, then numbered sections.
const LegalDocument = ({ eyebrow, title, updatedOn, summary, children }: LegalDocumentProps) => {
  return (
    <div className="bg-background text-foreground">
      <section className={`${CONTAINER} py-14 sm:py-20`}>
        <div className="flex items-center gap-3">
          <span className="bg-accent h-px w-6 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
            {eyebrow}
          </span>
        </div>
        <h1 className="authority-display mt-6 text-4xl leading-[1.08] sm:text-5xl">{title}</h1>
        <p className="text-muted-foreground mt-4 text-sm">Last updated {formatDate(updatedOn)}</p>
        <div className="bg-card mt-8 border p-5 text-base leading-relaxed sm:p-6">{summary}</div>
      </section>

      <div className={`${CONTAINER} flex flex-col gap-10 pb-20`}>{children}</div>
    </div>
  );
};

interface LegalSectionProps {
  number: number;
  title: string;
  children: ReactNode;
}

export const LegalSection = ({ number, title, children }: LegalSectionProps) => {
  return (
    <section className="border-t pt-8">
      <h2 className="text-xl font-medium">
        <span className="text-muted-foreground mr-3 tabular-nums">{number}.</span>
        {title}
      </h2>
      <div className="mt-4 flex flex-col gap-3 leading-relaxed [&_li]:ml-5 [&_li]:list-disc [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2">
        {children}
      </div>
    </section>
  );
};

export default LegalDocument;
